import React, { useState, useEffect } from 'react';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ReactComponent as MaleIcon } from '../../../assets/icons/male.svg';
import { ReactComponent as FemaleIcon } from '../../../assets/icons/female.svg';

const StepOne = ({ formData, updateFields }) => {
  const { userProfile } = useUserProfile();
  const ageGroups = [
    { label: "15-18" },
    { label: "19-29" },
    { label: "30-39" },
    { label: "40-49" },
    { label: "50-59" },
    { label: "60+" }
  ];

  useEffect(() => {
    if (userProfile?.name && !formData.name) {
      updateFields({ name: userProfile.name });
    }
    if (!formData.ageGroup) {
      updateFields({ ageGroup: ageGroups[0].label });
    }
  }, [userProfile, formData.name, formData.ageGroup, updateFields]);

  const [currentIndex, setCurrentIndex] = useState(
    formData.ageGroup ? ageGroups.findIndex(group => group.label === formData.ageGroup) : 0
  );
  const [isAnimating, setIsAnimating] = useState(false);

  const handleNext = () => {
    if (isAnimating || currentIndex === ageGroups.length - 1) return;
    setIsAnimating(true);
    setCurrentIndex(currentIndex + 1);
    updateFields({ ageGroup: ageGroups[currentIndex + 1].label });
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handlePrev = () => {
    if (isAnimating || currentIndex === 0) return;
    setIsAnimating(true);
    setCurrentIndex(currentIndex - 1);
    updateFields({ ageGroup: ageGroups[currentIndex - 1].label });
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleCardClick = (index) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    updateFields({ ageGroup: ageGroups[index].label });
    setTimeout(() => setIsAnimating(false), 300);
  };

  return (
    <div className="space-y-6 w-full">
      <div className="space-y-2">
        <label className="block text-lg aw-label">Name</label>
        <input
          type="text"
          value={formData.name || ''}
          onChange={(e) => updateFields({ name: e.target.value })}
          className="aw-input"
          placeholder="Enter your name"
        />
      </div>

      <div className="space-y-4">
        <label className="block text-lg aw-label">Gender</label>
        <div className="flex gap-6 justify-center">
          {[
            { value: 'male', Icon: MaleIcon, label: 'Male' },
            { value: 'female', Icon: FemaleIcon, label: 'Female' }
          ].map(({ value, Icon, label }) => {
            const selected = formData.gender === value;
            return (
              <button
                type="button"
                key={value}
                onClick={() => updateFields({ gender: value })}
                className={`aw-btn-3d rounded-xl flex flex-col items-center justify-center h-28 w-28
                  ${selected ? 'aw-btn-red' : 'aw-btn-dark'}`}
              >
                <Icon className="w-10 h-10 text-white" />
                <span className="mt-2 font-semibold tracking-wide">{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-lg aw-label">Age Group</label>
        <div className="relative h-32 rounded-2xl px-2"
          style={{
            background: 'linear-gradient(180deg, #f7f7f9 0%, #ececef 100%)',
            border: '1px solid #d8d8dc',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06), 0 1px 0 rgba(255,255,255,0.7)'
          }}
        >
          {/* Edge fades */}
          <div className="absolute left-0 top-0 h-full w-20 rounded-l-2xl pointer-events-none z-10"
            style={{ background: 'linear-gradient(to right, #f7f7f9, rgba(247,247,249,0))' }} />
          <div className="absolute right-0 top-0 h-full w-20 rounded-r-2xl pointer-events-none z-10"
            style={{ background: 'linear-gradient(to left, #ececef, rgba(236,236,239,0))' }} />

          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className={`aw-round-btn absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9
              ${currentIndex === 0 ? 'aw-round-disabled' : 'aw-round-red'}`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            onClick={handleNext}
            disabled={currentIndex === ageGroups.length - 1}
            className={`aw-round-btn absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9
              ${currentIndex === ageGroups.length - 1 ? 'aw-round-disabled' : 'aw-round-red'}`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Carousel */}
          <div className="relative w-full h-full overflow-hidden px-14">
            <div className="w-full h-full flex items-center justify-center">
              {ageGroups.map((ageGroup, index) => {
                const position = index - currentIndex;
                const selected = position === 0;
                return (
                  <div
                    key={ageGroup.label}
                    onClick={() => handleCardClick(index)}
                    className={`absolute w-32 transition-all duration-300 ease-in-out
                      ${Math.abs(position) <= 1 ? 'cursor-pointer' : ''}`}
                    style={{
                      transform: `translateX(${position * 100}%) scale(${selected ? 1 : 0.78})`,
                      opacity: Math.abs(position) > 1 ? 0 : 1 - Math.abs(position) * 0.45,
                      zIndex: 10 - Math.abs(position),
                      pointerEvents: Math.abs(position) <= 1 ? 'auto' : 'none'
                    }}
                  >
                    <div
                      className={`aw-card-option ${selected ? 'aw-card-option-selected' : 'aw-card-option-dark'}`}
                    >
                      <div className="flex flex-col items-center">
                        <span className="text-center text-xl font-bold">{ageGroup.label}</span>
                        <span className={`text-center text-xs mt-1 ${selected ? 'text-white/85' : 'text-gray-300'}`}>
                          years
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepOne;
